/** @domain plan -- Equity plan configuration: plan details, dates, share pools, evergreen, features, termination rules. */
import { z } from 'zod';
import { createDomainField } from './types';
import { trimExtraSpaces, wholeNumberOnly, wholeNumberWithCommas } from './formatters';

const define = createDomainField('plan');

// ---------------------------------------------------------------------------
// Core plan fields
// ---------------------------------------------------------------------------

export const planName = define({
  label: 'Plan Name',
  placeholder: 'Enter plan name',
  schema: z
    .string()
    .max(200, 'Must be at most 200 characters')
    .regex(/^\s*\S[\s\S]*$/, 'Must not be blank'),
  adapterHint: 'text',
  formatter: trimExtraSpaces,
  displayFormat: 'text',
});

export const planTypeCategoryIds = define({
  label: 'Plan Type(s)',
  schema: z.array(z.string()),
  adapterHint: 'multi-select',
  displayFormat: 'text',
});

export const planTerm = define({
  label: 'Plan Term',
  placeholder: 'Enter plan term',
  schema: z.string().regex(/^[0-9]*$/, 'Must be a whole number'),
  adapterHint: 'number',
  formatter: wholeNumberOnly,
  displayFormat: 'number',
});

// ---------------------------------------------------------------------------
// Plan dates
// ---------------------------------------------------------------------------

export const boardApprovalDate = define({
  label: 'Board Approval Date',
  schema: z.string(),
  adapterHint: 'date',
  displayFormat: 'date',
});

export const shareholderApprovalDate = define({
  label: 'Shareholder Approval Date',
  schema: z.string(),
  adapterHint: 'date',
  displayFormat: 'date',
});

export const planEffectiveDate = define({
  label: 'Plan Effective Date',
  schema: z.string(),
  adapterHint: 'date',
  displayFormat: 'date',
});

export const planExpiryDate = define({
  label: 'Plan Expiry Date',
  schema: z.string(),
  adapterHint: 'date',
  displayFormat: 'date',
});

// ---------------------------------------------------------------------------
// Share pool fields
// ---------------------------------------------------------------------------

export const numberOfSharesAuthorized = define({
  label: 'No. of Shares Authorized',
  placeholder: 'Enter shares authorized',
  schema: z.string().regex(/^[0-9,]*$/, 'Must contain only digits'),
  adapterHint: 'text',
  formatter: wholeNumberWithCommas,
  displayFormat: 'number',
});

export const numberOfSharesUnavailable = define({
  label: 'No. of Shares Unavailable',
  placeholder: 'Enter shares unavailable',
  schema: z.string().regex(/^[0-9,]*$/, 'Must contain only digits'),
  adapterHint: 'text',
  formatter: wholeNumberWithCommas,
  displayFormat: 'number',
});

export const numberOfSharesAvailable = define({
  label: 'No. of Shares Available',
  placeholder: 'Enter shares available',
  schema: z.string().regex(/^[0-9,]*$/, 'Must contain only digits'),
  adapterHint: 'text',
  formatter: wholeNumberWithCommas,
  displayFormat: 'number',
});

export const eligibleShareClass = define({
  label: 'Eligible Share Class',
  placeholder: 'Enter share class',
  schema: z.string().max(100, 'Must be at most 100 characters'),
  adapterHint: 'text',
  displayFormat: 'text',
});

// ---------------------------------------------------------------------------
// Evergreen plan fields
// ---------------------------------------------------------------------------

export const evergreenPlan = define({
  label: 'Evergreen Plan',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});

export const evergreenCriteria = define({
  label: 'Evergreen Criteria',
  placeholder: 'Enter criteria',
  schema: z.string().max(1000, 'Must be at most 1000 characters'),
  adapterHint: 'text',
  displayFormat: 'text',
});

// ---------------------------------------------------------------------------
// Plan features
// ---------------------------------------------------------------------------

export const dividendEligibility = define({
  label: 'Dividend Eligibility',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});

export const optionExpirationTerm = define({
  label: 'Option Expiration Term (months)',
  placeholder: 'Enter months',
  schema: z.string().regex(/^[0-9]*$/, 'Must be a whole number'),
  adapterHint: 'number',
  formatter: wholeNumberOnly,
  displayFormat: 'number',
});

// ---------------------------------------------------------------------------
// Plan termination expiration options (per-termination-type)
// ---------------------------------------------------------------------------

export const deathExpiration = define({
  label: 'Death',
  placeholder: 'Enter months',
  schema: z.string().regex(/^[0-9]*$/, 'Must be a whole number'),
  adapterHint: 'number',
  formatter: wholeNumberOnly,
  displayFormat: 'number',
});

export const disabilityExpiration = define({
  label: 'Disability',
  placeholder: 'Enter months',
  schema: z.string().regex(/^[0-9]*$/, 'Must be a whole number'),
  adapterHint: 'number',
  formatter: wholeNumberOnly,
  displayFormat: 'number',
});

export const retirementExpiration = define({
  label: 'Retirement',
  placeholder: 'Enter months',
  schema: z.string().regex(/^[0-9]*$/, 'Must be a whole number'),
  adapterHint: 'number',
  formatter: wholeNumberOnly,
  displayFormat: 'number',
});

export const voluntaryTerminationExpiration = define({
  label: 'Voluntary Termination',
  placeholder: 'Enter months',
  schema: z.string().regex(/^[0-9]*$/, 'Must be a whole number'),
  adapterHint: 'number',
  formatter: wholeNumberOnly,
  displayFormat: 'number',
});

export const involuntaryTerminationExpiration = define({
  label: 'Involuntary Termination',
  placeholder: 'Enter months',
  schema: z.string().regex(/^[0-9]*$/, 'Must be a whole number'),
  adapterHint: 'number',
  formatter: wholeNumberOnly,
  displayFormat: 'number',
});

export const withCauseExpiration = define({
  label: 'With Cause',
  placeholder: 'Enter months',
  schema: z.string().regex(/^[0-9]*$/, 'Must be a whole number'),
  adapterHint: 'number',
  formatter: wholeNumberOnly,
  displayFormat: 'number',
});

// ---------------------------------------------------------------------------
// Plan document edit permissions
// ---------------------------------------------------------------------------

export const allowEditPlanRelatedDocuments = define({
  label: 'Allow Edit Plan Related Documents',
  schema: z.boolean(),
  adapterHint: 'checkbox',
  displayFormat: 'boolean',
});

// ---------------------------------------------------------------------------
// Plan reference fields (used as dropdowns on award/grant forms)
// ---------------------------------------------------------------------------

export const planNameSelect = define({
  label: 'Plan Name',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});

export const planType = define({
  label: 'Plan Type',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});

export const poolId = define({
  label: 'Share Pool',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});

// ---------------------------------------------------------------------------
// Plan-level termination rule fields
// ---------------------------------------------------------------------------

export const terminationExpirationDays = define({
  label: 'Expiration (days)',
  placeholder: 'Enter days',
  schema: z.string().regex(/^[0-9]*$/, 'Must be a whole number'),
  adapterHint: 'number',
  formatter: wholeNumberOnly,
  displayFormat: 'number',
});

export const terminationExpirationMonths = define({
  label: 'Expiration (months)',
  placeholder: 'Enter months',
  schema: z.string().regex(/^[0-9]*$/, 'Must be a whole number'),
  adapterHint: 'number',
  formatter: wholeNumberOnly,
  displayFormat: 'number',
});
